import { Array as YArray } from 'yjs';
import { ColorSource, Container, Graphics, Point, PointData } from 'pixi.js';
import { Account } from 'src/sync/account';
import { Room } from 'src/sync/room';

export interface ITimedPointData extends PointData {
    time: number;
}

interface IBezierSegment {
    begin: PointData;
    control1: PointData;
    control2: PointData;
    end: PointData;
}

export class Laser {
    public static width = 25;
    public static delay = 1250;
    public static tension = 0.4;
    public color: ColorSource = 0xFF0000;
    public gfx = new Graphics();
    public data: ITimedPointData[] = [];

    constructor(public container: Container, public laserId: string) {
        container.addChild(this.gfx);
    }

    public animate() {
        if (this.data.length > 0) {
            const sliceIndex = Laser.getSliceIndex(this.data);
            this.data = this.data.slice(sliceIndex);
            this.gfx.clear();
            if (this.data.length > 0) {
                this.drawLaserPen(this.data);
            } else if (Account.id == this.laserId) {
                const roomLaser = Room.lasers.get(Account.id) as YArray<PointData>;
                roomLaser.delete(0, roomLaser.length);
            }
        }
    }

    public static getSliceIndex(points: ITimedPointData[]): number {
        const timeThreshold = Date.now() - this.delay;
        for (let index = 0; index < points.length; index++) {
            if (points[index].time >= timeThreshold) return index;
        }
        return points.length;
    }

    public drawLaserPen(points: PointData[]) {
        const segments = this.getBezierSegments(points);
        const total = segments.length;
        if (segments.length < 1) return;

        this.gfx.moveTo(segments[0].begin.x, segments[0].begin.y)
        for (let i = 0; i < total; i++) {
            const seg = segments[i];
            const end = segments[i + 1]?.begin ?? seg.end;
            const time = (i + 1) / total;
            const alpha = Math.pow(time, 0.3);
            const width = (time * Laser.width);

            this.gfx
                .bezierCurveTo(seg.control1.x, seg.control1.y, seg.control2.x, seg.control2.y, end.x, end.y)
                .stroke({ width, color: this.color, alpha })
                .moveTo(end.x, end.y);
        }

        if (points.length) {
            const point = points[points.length - 1];
            this.gfx.circle(point.x, point.y, Laser.width * 0.6).fill({ color: this.color });
        }
    }

    public getBezierSegments(points: PointData[]): IBezierSegment[] {
        const result: IBezierSegment[] = [];

        if (points.length < 2) return result;

        for (let i = 0; i < points.length - 1; i++) {
            const handle1 = points[i - 1] ?? points[i];
            const begin = points[i];
            const end = points[i + 1];
            const handle2 = points[i + 2] ?? points[i + 1];

            const control1 = new Point(
                begin.x + (end.x - handle1.x) * Laser.tension / 3,
                begin.y + (end.y - handle1.y) * Laser.tension / 3
            );

            const control2 = new Point(
                end.x - (handle2.x - begin.x) * Laser.tension / 3,
                end.y - (handle2.y - begin.y) * Laser.tension / 3
            );

            result.push({ begin, end, control1, control2 });
        }

        return result;
    }
}
